/*
 * File: CreateHopeMessage.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 20, 2025 at 11:54 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 31, 2025 at 9:54 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, forwardRef, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';

// Utilities
import { isValidNode, parseMarkdownLinks } from '../../../utilities/parser';
import { stripHtml } from '../../../../utilities/utilities';

// Components
import { Typography } from '../../Typography';
import { Button } from '../../Button';
import { LocaleLink } from '../../LocaleLink';
import { TextArea } from '../../TextArea';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../constants/base.constants';

// Styles
import * as S from './CreateHopeMessage.styles';


/**
 * Configuration
 */

// Parser
const ReactParser = new Parser();

// Node Definitions
const processNodeDefinitions = new ProcessNodeDefinitions();

// Parsing Instructions
const ParsingInstructions = [
	{
		shouldProcessNode(node) {
			return node.name === 'a';
		},
		processNode(node, children) {
			return <LocaleLink key={node.attribs.href} to={node.attribs.href} target="_blank">{children}</LocaleLink>;
		}
	},
	{
		shouldProcessNode() { return true; },
		processNode: processNodeDefinitions.processDefaultNode
	}
];


/**
 * Component
 */

export const CreateHopeMessage = forwardRef(({
	className, moveForward, moveBackward, hopeMessage, setHopeMessage, isVisible
}, ref) => {

	// Create state handlers
	const [showInput, setShowInput] = useState(isVisible);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get partner content
	const partnerContent = useMemo(() => partner?.content?.pages?.share_story?.components?.create_hope_message, [partner]);
	const warningContent = useMemo(() => partner?.content?.pages?.share_story?.components?.warnings, [partner]);

	// Get legal urls
	const reviewGuidelinesUrl = useMemo(() => (partner?.legal?.links?.review_guidelines ? partner?.legal?.links?.review_guidelines : `https://www.ourwave.org${locale.localePath}/review-guidelines`), [partner, locale]);
	const hasInternalLegalPages = useMemo(() => partner?.legal?.has_internal_legal_pages, [partner]);

	// Handle next action
	const nextAction = () => {

		// Move to next step
		moveForward();

	};

	// Perform actions on visibility change
	useEffect(() => {
		if (isVisible) {

			// Update show input
			setShowInput(true);

		} else {

			// Update show input
			setTimeout(() => {
				setShowInput(false);
			}, 300);

		}
	}, [isVisible]);

	// Perform actions on component load
	useEffect(() => {}, []);

	// Return component
	return (
		<S.StepContainer ref={ref} className={className}>
			<S.InnerStep>

				{/* Content Container */}
				<S.ContentContainer>

					{/* Title */}
					<Typography tag="h1" weight="black">
						{!partnerContent?.title ? (
							<Trans t={t} i18nKey="pageShareStory.createHopeMessage.title">
								We believe in hope.
							</Trans>
						) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.title[locale.localeBaseId]), isValidNode, ParsingInstructions)}
					</Typography>

					{/* Subtitle */}
					<Typography tag="p">
						{!partnerContent?.subtitle ? (
							<Trans t={t} i18nKey="pageShareStory.createHopeMessage.subtitle">
								Would you like to share a message of hope with another survivor? This could be part of your own story, or something new.
								{' '}
								<strong>This is completely optional.</strong>
							</Trans>
						) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.subtitle[locale.localeBaseId]), isValidNode, ParsingInstructions)}
					</Typography>

				</S.ContentContainer>

				{/* Inputs */}
				{showInput && (
					<S.InputContainer>
						<TextArea
							placeholder={t('pageShareStory.createHopeMessage.placeholder')}
							name="message"
							type="text"
							rows={10}
							value={hopeMessage || ''}
							onChange={(event) => {
								const { value } = event.target;
								setHopeMessage(stripHtml(value));
							}}
							autoResize
						/>
					</S.InputContainer>
				)}

				{/* Warnings */}
				<S.Warnings>

					{/* Minor Warning */}
					<Typography tag="p" variation="3">
						<Trans t={t} i18nKey="pageShareStory.minorWarning">
							To protect our community, we are only able to post stories by survivors who are currently at least 18 years old. For more info, check out our
							{' '}
							<LocaleLink to={reviewGuidelinesUrl} target={!hasInternalLegalPages ? '_blank' : null}>Review Guidelines</LocaleLink>
							.
						</Trans>
					</Typography>

					{/* Partner Warnings */}
					{warningContent && warningContent.length > 0 && warningContent.map((warning) => (
						<Typography tag="p" variation="3" key={warning.id}>
							{warning[locale.localeBaseId]}
						</Typography>
					))}

				</S.Warnings>

				{/* Continue Button */}
				<S.ActionContainer>
					<Button size="large" variant="text" onClick={moveBackward}>
						<FontAwesomeIcon icon={['fas', 'arrow-left-long']} />
						<Typography>
							<Trans t={t} i18nKey="actions.back">
								Back
							</Trans>
						</Typography>
					</Button>
					<Button size="large" onClick={nextAction}>
						<Typography>
							<Trans t={t} i18nKey="actions.continue">
								Continue
							</Trans>
						</Typography>
						<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
					</Button>
				</S.ActionContainer>

				{/* Legal Snippet */}
				<S.LegalSnippet />

			</S.InnerStep>
		</S.StepContainer>
	);
});


/**
 * Configuration
 */

CreateHopeMessage.displayName = 'CreateHopeMessage';
CreateHopeMessage.propTypes = {
	className: PropTypes.string,
	moveForward: PropTypes.func,
	moveBackward: PropTypes.func,
	hopeMessage: PropTypes.string,
	setHopeMessage: PropTypes.func,
	isVisible: PropTypes.bool
};
CreateHopeMessage.defaultProps = {
	className: null,
	moveForward: null,
	moveBackward: null,
	hopeMessage: null,
	setHopeMessage: null,
	isVisible: false
};
